import React, { type FC, type MouseEvent, useState } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button';
import FeedbackCollector from '@atlaskit/feedback-collector';
import { useFlags } from '@atlaskit/flag';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { G300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useAIFeedbackProperties } from '../feedback-properties-provider';

import { ThumbIcon } from './assets/ThumbIcon';

const LINK_TO_PRIVACY_POLICY = 'https://www.atlassian.com/legal/privacy-policy';
const LINK_TO_RESEARCH_GROUP = 'https://www.atlassian.com/research-group';

const i18n = defineMessages({
	flagTitle: {
		id: 'quick-summary.feedback.flag.title',
		defaultMessage: 'Thanks!',
		description: 'Title of the success notification that appears after users submit AI feedback.',
	},
	flagDescription: {
		id: 'quick-summary.feedback.flag.description',
		defaultMessage: 'We appreciate you taking the time to send this feedback.',
		description:
			'Description text in the feedback success notification expressing gratitude for user feedback.',
	},
	likeTooltip: {
		id: 'quick-summary.like.tooltip',
		defaultMessage: 'Helpful',
		description: 'Tooltip for the thumbs-up button that rates AI responses as good or helpful.',
	},
	dislikeTooltip: {
		id: 'quick-summary.dislike.tooltip',
		defaultMessage: 'Not helpful',
		description: 'Tooltip for the thumbs-down button that rates AI responses as bad or unhelpful.',
	},
	feedbackTitle: {
		id: 'quick-summary.feedback.title',
		defaultMessage: 'Help us improve Atlassian Intelligence',
		description: 'Label text for the summary section of the report feedback collector',
	},
	feedbackSummaryTitle: {
		id: 'quick-summary.feedback.summary.title',
		defaultMessage: 'Tell us about your experience',
		description: 'Title text for the summary section of the report feedback collector',
	},
	feedbackSummaryPlaceholder: {
		id: 'quick-summary.feedback.summary.placeholder',
		defaultMessage: 'What did Atlassian Intelligence do well? What could be improved?',
		description: 'Description text for the summary section of the report feedback collector',
	},
	canContactText: {
		id: 'quick-summary.feedback.can-contact.label',
		defaultMessage: 'Atlassian can contact me about this feedback. See our {privacyPolicyLink}.',
		description:
			"Text next to first checkbox. {privacyPolicyLink} will be substituted with a link whose text reads 'privacy policy'",
	},
	privacyPolicyLink: {
		id: 'quick-summary.feedback.privacy-policy.link',
		defaultMessage: 'privacy policy',
		description: 'The text of the link that will direct users to the privacy policy site.',
	},
	improveAtlassianText: {
		id: 'quick-summary.feedback.improve-atlassian',
		defaultMessage:
			'I’d like to help improve Atlassian products by joining the {researchGroupLink}.',
		description:
			"Text next to second checkbox. {researchGroupLink} will be substituted with a message which reads as 'Atlassian Research Group'",
	},
	improveAtlassianTextAppify: {
		id: 'quick-summary.feedback.improve-atlassian-appify',
		defaultMessage: 'I’d like to help improve Atlassian apps by joining the {researchGroupLink}.',
		description:
			"Text next to second checkbox. {researchGroupLink} will be substituted with a message which reads as 'Atlassian Research Group'",
	},
	researchGroupLink: {
		id: 'quick-summary.feedback.research-group.link',
		defaultMessage: 'Atlassian Research Group',
		description: 'The text of the link that will direct users to the research group site.',
	},
});

type LinkType = {
	link: string;
};

const PrivacyPolicyLink = ({ link }: LinkType) =>
	fg('dst-a11y__replace-anchor-with-link__confluence-ai') ? (
		<Link target="_blank" href={link}>
			<FormattedMessage {...i18n.privacyPolicyLink} />
		</Link>
	) : (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
		<a target="_blank" href={link}>
			<FormattedMessage {...i18n.privacyPolicyLink} />
		</a>
	);

const ResearchGroupLink = ({ link }: LinkType) =>
	fg('dst-a11y__replace-anchor-with-link__confluence-ai') ? (
		<Link target="_blank" href={link}>
			<FormattedMessage {...i18n.researchGroupLink} />
		</Link>
	) : (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
		<a target="_blank" href={link}>
			<FormattedMessage {...i18n.researchGroupLink} />
		</a>
	);

const CanContactLabel = () => {
	return (
		<FormattedMessage
			{...i18n.canContactText}
			values={{
				privacyPolicyLink: <PrivacyPolicyLink link={LINK_TO_PRIVACY_POLICY} />,
			}}
		/>
	);
};

const EnrolLabel = () => {
	return (
		<FormattedMessage
			{...(fg('product-terminology-refresh')
				? i18n.improveAtlassianTextAppify
				: i18n.improveAtlassianText)}
			values={{
				researchGroupLink: <ResearchGroupLink link={LINK_TO_RESEARCH_GROUP} />,
			}}
		/>
	);
};

const DislikeIconWrapperXCSS = xcss({
	transform: 'rotate(180deg)',
});

const ButtonWrapperXCSS = xcss({
	height: '24px',
	width: '24px',
});

type Sentiment = 'good' | 'bad' | '';

export type AIFeedbackComponentProps = {
	contentId: string;
	onDislikeClicked?: () => void;
	onLikeClicked?: () => void;
};

export const AIFeedbackComponent: FC<AIFeedbackComponentProps> = ({
	contentId,
	onLikeClicked,
	onDislikeClicked,
}) => {
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
	const feedbackCollectorProperties = useAIFeedbackProperties();
	const [sentiment, setSentiment] = useState<Sentiment>('');
	const [hasGoodFeedbackBeenSubmitted, setHasGoodFeedbackBeenSubmitted] = useState(false);
	const [hasBadFeedbackBeenSubmitted, setHasBadFeedbackBeenSubmitted] = useState(false);

	const feedbackContextString = `contentId: ${contentId} sentiment: ${sentiment}${
		feedbackCollectorProperties.feedbackContext.length > 0
			? ` ${feedbackCollectorProperties.feedbackContext}`
			: ''
	}`;

	const showFeedbackFlag = () => {
		showFlag({
			title: formatMessage(i18n.flagTitle),
			description: formatMessage(i18n.flagDescription),
			icon: (
				<StatusSuccessIcon
					spacing="spacious"
					color={token('color.icon.success', G300)}
					label="success"
				/>
			),
			isAutoDismiss: true,
		});
	};

	const handleLikeClicked = () => {
		onLikeClicked?.();
		setIsFeedbackOpen(true);
		setSentiment('good');
	};

	const handleDislikeClicked = () => {
		onDislikeClicked?.();
		setIsFeedbackOpen(true);
		setSentiment('bad');
	};

	const onFeedbackSubmit = () => {
		showFeedbackFlag();
		setIsFeedbackOpen(false);

		if (sentiment === 'good') {
			setHasGoodFeedbackBeenSubmitted(true);
			setHasBadFeedbackBeenSubmitted(false);
		} else {
			setHasBadFeedbackBeenSubmitted(true);
			setHasGoodFeedbackBeenSubmitted(false);
		}
	};

	return (
		<Box role="none" onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}>
			<Inline alignBlock="center" space="space.050">
				<Tooltip hideTooltipOnMouseDown content={formatMessage(i18n.likeTooltip)} position="top">
					<Box xcss={ButtonWrapperXCSS}>
						<Button
							appearance="subtle"
							aria-label={formatMessage(i18n.likeTooltip)}
							spacing="none"
							iconBefore={<ThumbIcon isSelected={hasGoodFeedbackBeenSubmitted} />}
							onClick={handleLikeClicked}
							testId="feedback-like-button"
							isDisabled={hasGoodFeedbackBeenSubmitted}
						/>
					</Box>
				</Tooltip>
				<Tooltip hideTooltipOnMouseDown content={formatMessage(i18n.dislikeTooltip)} position="top">
					<Box xcss={ButtonWrapperXCSS}>
						<Button
							appearance="subtle"
							aria-label={formatMessage(i18n.dislikeTooltip)}
							spacing="none"
							iconBefore={
								<Box xcss={DislikeIconWrapperXCSS}>
									<ThumbIcon isSelected={hasBadFeedbackBeenSubmitted} />
								</Box>
							}
							onClick={handleDislikeClicked}
							testId="feedback-dislike-button"
							isDisabled={hasBadFeedbackBeenSubmitted}
						/>
					</Box>
				</Tooltip>
				{isFeedbackOpen && (
					<FeedbackCollector
						locale={feedbackCollectorProperties.locale}
						onSubmit={onFeedbackSubmit}
						onClose={() => setIsFeedbackOpen(false)}
						atlassianAccountId={feedbackCollectorProperties.userId}
						name={feedbackCollectorProperties.displayName}
						entrypointId={feedbackCollectorProperties.entrypointId}
						summaryPlaceholder={formatMessage(i18n.feedbackSummaryPlaceholder)}
						additionalFields={[
							{
								id: feedbackCollectorProperties.feedbackContextId,
								value: feedbackContextString,
							},
						]}
						feedbackTitle={formatMessage(i18n.feedbackTitle)}
						showTypeField={false}
						customTextAreaLabel={formatMessage(i18n.feedbackSummaryTitle)}
						canBeContactedLabel={<CanContactLabel />}
						enrolInResearchLabel={<EnrolLabel />}
					/>
				)}
			</Inline>
		</Box>
	);
};
